import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import CopyButton from "@components/buttons/CopyButton";
import { billingServices } from "@services/apis/Billing";
import { GetExpenseData } from "@services/models/Billing/AdminExpensesModel";
import ConstantFunctions from "../../utils/constants_functions";
import { ColDef } from "ag-grid-community";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { buildCSV, CSVColumn, downloadCSV } from "../../utils/downloadCSV";
import { BillingHandle } from "./Billing";

const ROWS = 100;

const Expenses = forwardRef<BillingHandle>((_, ref) => {
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const constantFunctions = new ConstantFunctions();

  const [data, setData] = useState<GetExpenseData[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const gridRef = useRef<any>(null);
  const didInitialFetch = useRef(false);
  const inFlight = useRef(false);

  // ✅ NEW: hard stop + page guard
  const stopRef = useRef(false);
  const pageRequestedRef = useRef(0);

  const fetchExpenses = async (nextOffset: number, reset = false) => {
    if (inFlight.current) return;
    if (!reset && stopRef.current) return;

    const page = Math.floor(nextOffset / ROWS) + 1;

    // ✅ prevent requesting same/older page again
    if (!reset && page <= pageRequestedRef.current) return;

    inFlight.current = true;
    pageRequestedRef.current = page;

    try {
      setError(null);
      if (reset) setLoading(true);
      else setLoadingMore(true);

      const res = await billingServices.getAdminExpenses(page, ROWS);

      const rows: GetExpenseData[] = res.data.expenses ?? [];
      const p = res.data.pagination;

      // ✅ prefer backend hasMore, fallback to row count
      const apiHasMore =
        typeof p?.hasMore === "boolean" ? p.hasMore : rows.length === ROWS;

      // ✅ hard stop if backend says no more OR returned empty page
      if (!apiHasMore || rows.length === 0) {
        stopRef.current = true;
      }

      setHasMore(apiHasMore && rows.length > 0);
      setData((prev) => (reset ? rows : [...prev, ...rows]));
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
      setLoadingMore(false);
      inFlight.current = false;
    }
  };

  // initial load
  useEffect(() => {
    if (didInitialFetch.current) return;
    didInitialFetch.current = true;

    // ✅ reset guards
    stopRef.current = false;
    pageRequestedRef.current = 0;

    setOffset(0);
    setHasMore(true);
    fetchExpenses(0, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // fetch when offset changes
  useEffect(() => {
    if (!didInitialFetch.current) return;
    if (offset === 0) return;
    fetchExpenses(offset, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  const onBodyScrollEnd = useCallback((params: any) => {
    if (stopRef.current || inFlight.current) return;

    const api = params.api;
    const lastRow = api.getDisplayedRowCount() - 1;
    const lastVisible = api.getLastDisplayedRowIndex();

    if (lastVisible >= lastRow - 1) {
      setOffset(pageRequestedRef.current * ROWS);
    }
  }, []);
  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Date",
        field: "expenseDate",
        width: 110,
        filter: "agTextColumnFilter",
        valueFormatter: (params) => {
          if (!params.value) return "";
          return constantFunctions.dateFormatInDDMMYY(params.value);
        },
      },

      {
        headerName: "Expense ID",
        field: "id",
        width: 90,
        filter: "agNumberColumnFilter",
      },

      {
        headerName: "User ID",
        field: "uid",
        width: 90,
        filter: "agNumberColumnFilter",
        cellRenderer: (params: any) => (
          <div className="flex items-center gap-2 h-full">
            <span>{params.value}</span>
            <CopyButton
              copyText={params.value?.toString() || ""}
              copyMessage="User ID copied!"
              copyIconSize={10}
              buttonSize="14px"
              borderRadius="2px"
            />
          </div>
        ),
      },

      {
        headerName: "Merchant ID",
        field: "mid",
        width: 100,
        valueFormatter: (p) => p.value || "-",
      },

      {
        headerName: "Legal Entity Name",
        field: "legalEntityName",
        minWidth: 180,
        wrapText: true,
        autoHeight: true,
        valueFormatter: (p) => p.value || "-",
      },

      {
        headerName: "Account Name",
        field: "accountUsed",
        minWidth: 150,
        wrapText: true,
        autoHeight: true,
      },

      {
        headerName: "DBA",
        field: "dba",
        minWidth: 140,
        valueFormatter: (p) => p.value || "-",
      },

      {
        headerName: "Location",
        field: "location",
        minWidth: 140,
        valueFormatter: (p) => p.value || "-",
      },

      {
        headerName: "Service",
        field: "service",
        width: 140,
      },

      {
        headerName: "Tag",
        field: "category",
        width: 110,
      },

      {
        headerName: "User Notes",
        field: "notes",
        flex: 1,
        minWidth: 220,
        wrapText: true,
        autoHeight: true,
      },

      {
        headerName: "Amount",
        field: "amount",
        width: 120,
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => {
          const value = parseFloat(params.value);
          return isNaN(value) ? params.value : `$${value.toLocaleString()}`;
        },
      },

      {
        headerName: "Currency",
        field: "currencyCode",
        width: 90,
      },
      {
        headerName: "Status",
        field: "direction",
        minWidth: 140,
        wrapText: true,
        autoHeight: true,
      },
    ],
    [],
  );

  const exportToCSV = async () => {
    const EXPORT_ROWS = 1000;
    let allRows: GetExpenseData[] = [];
    let currentPage = 1;
    let hasMoreData = true;

    while (hasMoreData) {
      const res = await billingServices.getAdminExpenses(
        currentPage,
        EXPORT_ROWS,
      );
      const rows = res.data.expenses ?? [];
      allRows = [...allRows, ...rows];
      hasMoreData = res.data.pagination?.hasMore ?? false;
      currentPage++;
    }

    const csvColumns: CSVColumn[] = [
      {
        header: "Date",
        field: "expenseDate",
        formatter: (v) => (v ? constantFunctions.dateFormatInDDMMYY(v) : ""),
      },
      { header: "Expense ID", field: "id" },
      { header: "User ID", field: "uid" },
      { header: "Merchant ID", field: "mid" },
      { header: "Legal Entity Name", field: "legalEntityName" },
      { header: "Account Name", field: "accountUsed" },
      { header: "DBA", field: "dba" },
      { header: "Location", field: "location" },
      { header: "Service", field: "service" },
      { header: "Tag", field: "category" },
      { header: "User Notes", field: "notes" },
      { header: "Amount", field: "amount" },
      { header: "Currency", field: "currencyCode" },
      { header: "Status", field: "direction" },
    ];

    const date = new Date();
    const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    const csv = buildCSV(allRows, csvColumns);
    downloadCSV(csv, `expenses-data-${formattedDate}.csv`);
  };

  useImperativeHandle(ref, () => ({ exportToCSV }));

  const onGridReady = useCallback((params: any) => {
    gridRef.current = params;
  }, []);

  return (
    <div
      className="flex-1 py-4 overflow-hidden"
      style={{ height: "calc(100dvh - 70px)" }}
    >
      {loading && offset === 0 ? (
        <AgGridSkeleton
          columnDefs={columnDefs}
          style={{ height: "calc(100vh - 150px)" }}
        />
      ) : (
        <div className="flex flex-col h-full">
          <AgGridReactTable
            ref={gridRef}
            rowData={data}
            columnDefs={columnDefs}
            onGridReady={onGridReady}
            onBodyScroll={onBodyScrollEnd}
            error={error ?? undefined}
            style={{ height: "calc(100vh - 150px)" }}
            getRowId={(params) => params.data.id?.toString()}
          />

          {loadingMore && data.length > 0 && (
            <div className="flex items-center justify-center py-4 border-t border-border-subtle">
              <div className="flex items-center space-x-2 text-text-subtle">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-border-subtle border-t-border-button-primary" />
                <span className="text-sm">Loading more expenses...</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

Expenses.displayName = "Expenses";

export default Expenses;
